import { Component } from '@theme/component';
const config = JSON.parse(document.querySelector('[data-one-wa-config]')?.textContent || '{}');
const t = config.text || {};
const currencyText = (value, currency) => new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency }).format(Number(value) / 100);
const validPhone = (phone) => /^\d{8,15}$/.test(phone || '');
const publishClick = (type, detail) => {
  window.dataLayer?.push?.({ event: 'whatsapp_order_click', order_type: type, ...detail });
  window.Shopify?.analytics?.publish?.('whatsapp_order_click', { order_type: type, ...detail });
};
class OneWhatsAppProduct extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.controller?.abort();
    this.controller = new AbortController();
    const signal = this.controller.signal;
    this.readState();
    this.section = this.closest('.shopify-section') || document;
    this.section.addEventListener('variant:selected', () => { this.pending = true; this.updateLink(); }, { signal });
    this.section.addEventListener('variant:update', (event) => {
      const detail = event.detail;
      if (String(detail?.data?.productId) !== this.dataset.productId && String(detail?.data?.newProduct?.id) !== this.dataset.productId) return;
      const fresh = Array.from(detail.data.html?.querySelectorAll('one-whatsapp-product') || []).find((element) => element.dataset.productId === this.dataset.productId);
      if (fresh) this.state = JSON.parse(fresh.querySelector('[data-one-wa-product]').textContent);
      this.pending = false;
      this.updateLink();
    }, { signal });
    this.closest('form')?.addEventListener('input', () => this.updateLink(), { signal });
    this.closest('form')?.addEventListener('change', () => this.updateLink(), { signal });
    this.closest('form')?.addEventListener('quantity-selector:update', () => this.updateLink(), { signal });
    this.addEventListener('click', (event) => {
      if (!(event.target instanceof Element) || !event.target.closest('[data-one-wa-order]')) return;
      this.updateLink();
      if (this.disabled) { event.preventDefault(); this.status(t.choose); return; }
      publishClick('product', { product_id: this.dataset.productId, variant_id: this.state.variant.id, quantity: this.quantity });
    }, { signal });
    this.updateLink();
  }
  disconnectedCallback() { super.disconnectedCallback(); this.controller?.abort(); }
  readState() { this.state = JSON.parse(this.querySelector('[data-one-wa-product]').textContent); }
  status(message) { this.querySelector('[data-one-wa-status]').textContent = message || ''; }
  updateLink() {
    const link = this.querySelector('[data-one-wa-order]');
    const form = this.closest('form');
    const variantId = form?.querySelector('input[name="id"]')?.value;
    const quantityInput = form?.querySelector('input[name="quantity"]');
    this.quantity = Number(quantityInput?.value || 1);
    const variant = this.state.variant;
    link.textContent = variant.price > 0 ? t.order : t.ask_price;
    const invalidQuantity = !Number.isInteger(this.quantity) || this.quantity < 1 || (quantityInput && !quantityInput.checkValidity());
    this.disabled = this.pending || !variant?.available || !validPhone(this.dataset.phone) || invalidQuantity || (variantId && String(variant.id) !== variantId);
    link.setAttribute('aria-disabled', String(Boolean(this.disabled)));
    link.tabIndex = this.disabled ? -1 : 0;
    if (this.disabled) return;
    const url = new URL(this.state.url, location.origin);
    url.searchParams.set('variant', variant.id);
    const lines = [t.hello, this.state.title];
    if (variant.title && variant.title !== 'Default Title') lines.push(variant.title);
    lines.push(`${t.quantity}: ${this.quantity}`);
    if (variant.price > 0) lines.push(`${t.unit_price}: ${currencyText(variant.price, this.state.currency)}`, `${t.subtotal}: ${currencyText(variant.price * this.quantity, this.state.currency)}`);
    else lines.push(t.price_pending);
    lines.push(url.href, t.confirm);
    link.href = `https://wa.me/${this.dataset.phone}?text=${encodeURIComponent(lines.join('\n'))}`;
    this.status('');
  }
}
class OneWhatsAppCart extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.controller?.abort();
    this.controller = new AbortController();
    const signal = this.controller.signal;
    this.addEventListener('click', async (event) => {
      if (!(event.target instanceof Element)) return;
      if (event.target.closest('[data-one-wa-cart-send]')) { this.send(); return; }
      if (!event.target.closest('[data-one-wa-copy-button]')) return;
      const input = this.querySelector('[data-one-wa-message]');
      input.focus(); input.select();
      try { await navigator.clipboard.writeText(input.value); this.status(t.copied); } catch { this.status(t.copy_manually); }
    }, { signal });
  }
  disconnectedCallback() { super.disconnectedCallback(); this.controller?.abort(); }
  status(message) { this.querySelector('[data-one-wa-status]').textContent = message || ''; }
  async send() {
    const button = this.querySelector('[data-one-wa-cart-send]');
    if (button.disabled) return;
    if (!validPhone(this.dataset.phone)) { this.status(t.error); return; }
    button.disabled = true; button.setAttribute('aria-busy', 'true'); this.status(t.loading);
    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' }, cache: 'no-store', signal: this.controller.signal });
      if (!response.ok) throw new Error('Cart unavailable');
      const cart = await response.json();
      if (!cart.items?.length) { this.status(t.empty); return; }
      const lines = [t.cart_hello, ''];
      cart.items.forEach((item, index) => {
        const url = new URL(item.url || `/products/${item.handle}`, location.origin);
        url.searchParams.set('variant', item.variant_id);
        lines.push(`${index + 1}. ${item.product_title}`);
        if (item.variant_title && item.variant_title !== 'Default Title') lines.push(item.variant_title);
        lines.push(`${t.quantity}: ${item.quantity}`);
        if (item.original_price === 0) lines.push(t.price_pending);
        else lines.push(`${t.unit_price}: ${currencyText(item.final_price, cart.currency)}`, `${t.subtotal}: ${currencyText(item.final_line_price, cart.currency)}`);
        lines.push(url.href, '');
      });
      lines.push(`${t.total}: ${currencyText(cart.total_price, cart.currency)}`);
      if (cart.note) lines.push(`${t.note}: ${cart.note}`);
      lines.push(t.confirm);
      const message = lines.join('\n');
      publishClick('cart', { item_count: cart.item_count, value: cart.total_price / 100, currency: cart.currency });
      const target = `https://wa.me/${this.dataset.phone}?text=${encodeURIComponent(message)}`;
      if (target.length > 8000) {
        this.querySelector('[data-one-wa-message]').value = message;
        this.querySelector('[data-one-wa-copy]').hidden = false;
        this.status(t.long_message);
      } else { this.status(''); location.assign(target); }
    } catch { this.status(t.error); }
    finally { button.disabled = false; button.removeAttribute('aria-busy'); }
  }
}
if (!customElements.get('one-whatsapp-product')) customElements.define('one-whatsapp-product', OneWhatsAppProduct);
if (!customElements.get('one-whatsapp-cart')) customElements.define('one-whatsapp-cart', OneWhatsAppCart);
